import { ApolloProvider } from '@apollo/client';
import {
  AIAssistantEmbeddedPanel,
  AppUserProvider,
  AuthRoute,
  ClearToast,
  ConfigProvider,
  Content,
  EnvProvider,
  ErrorBoundary,
  NetworkStatus,
  NotFound,
  ProjectListing,
  Settings,
  useAIAssistantContext,
  useAppUser,
} from '@bechtel/client-common';
import {
  createTheme,
  GlobalStyles as GDSGlobalStyles,
  Spinner,
  SpinnerSize,
  ToastContent,
  ToastProvider,
  useModalState,
} from '@bechtel/gds';
import { PersonaType } from '@bechtel/types';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { LicenseManager } from 'ag-grid-enterprise';
import { find } from 'lodash';
import { lazy, Suspense, useEffect } from 'react';
import { hot } from 'react-hot-loader/root';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { SubscriptionManager } from '~/components/atoms/subscription-manager';
import { ApplicationGlobalStyles } from '~/components/global-styles';
import { Nav } from '~/components/organisms/nav';
import { client } from './apollo-client';
import { envConfig } from './env-config';
import { defaultLocale, dynamicActivate } from './internationalization';
// NOTE: always async load route components  except initial entry route

const Auth = lazy(() => import('./views/auth'));
const WorkPackages = lazy(() => import('@bechtel/client-work-packages'));
const Welds = lazy(() => import('@bechtel/client-welds'));
const Blocks = lazy(() => import('@bechtel/client-blocks'));
const Timesheet = lazy(() => import('@bechtel/client-timesheet'));
const CrewChange = lazy(() => import('@bechtel/client-crew-change'));
const ProjectAdmin = lazy(() => import('@bechtel/client-project-admin'));
const DashboardView = lazy(() => import('@bechtel/client-weld-dashboard'));
const RodRoomView = lazy(() => import('@bechtel/client-rod-room'));
const ReportView = lazy(() => import('@bechtel/client-reports'));
const theme = createTheme();
LicenseManager.setLicenseKey(envConfig.agGridLicenseKey);

const AuthenticatedApp = () => {
  const { user } = useAppUser();
  const { close, toggle, visible } = useModalState(false);
  const { close: settingsClose, toggle: settingsOpen, visible: settingsVisible } = useModalState(false);
  const { close: aiClose, toggle: aiToggle, visible: aiVisible } = useModalState(false);
  const isRodRoomAttendant = user?.crewMember?.persona === PersonaType.ROD_ROOM_ATTENDANT;
  let showNav = true;

  // Initialize AI Assistant context tracking
  useAIAssistantContext({ application: 'as-client', autoUpdate: true });

  if (isRodRoomAttendant) {
    showNav = false;
  }

  useEffect(() => {
    const userLanguage = user?.userPreference?.language;
    if (userLanguage) {
      const languages = [
        { lang: 'english', locale: 'en' },
        { lang: 'spanish', locale: 'es' },
      ];
      const locale = find(languages, (language) => language.lang === userLanguage)?.locale;
      dynamicActivate(locale ? locale : defaultLocale);
    }
  }, [user]);

  //Open one of the drawer either settings/project picker on the top
  useEffect(() => {
    if (visible) settingsClose();
  }, [visible]);

  useEffect(() => {
    if (settingsVisible) close();
  }, [settingsVisible]);

  const handleViewProfile = () => settingsOpen();
  const handleLanguageSelect = (lang) => {
    dynamicActivate(lang.locale);
  };

  return (
    <ErrorBoundary>
      <SubscriptionManager>
        <ToastProvider>
          <GDSGlobalStyles />
          <ApplicationGlobalStyles />
          {showNav && <Nav toggle={toggle} onViewProfile={handleViewProfile} onOpenAIAssistant={aiToggle} />}
          <ClearToast />
          <Content>
            <ErrorBoundary>
              <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                <Routes>
                  <Route element={<AuthRoute />}>
                    <Route index element={isRodRoomAttendant ? <RodRoomView /> : <Welds />} />
                    <Route path="work-packages/*" element={<WorkPackages />} />
                    <Route path="blocks" element={<Blocks />} />
                    <Route path="crew-change/*" element={<CrewChange />} />
                    <Route path="timesheet" element={<Timesheet />} />
                    <Route path="project-admin/*" element={<ProjectAdmin />} />
                    <Route path="/*" element={<Welds />} />
                    <Route path="dashboard/*" element={<DashboardView />} />
                    <Route path="rod-room/*" element={<RodRoomView />} />
                    <Route path="reports/*" element={<ReportView />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
                {visible && <ProjectListing close={close} />}
                {settingsVisible && (
                  <Settings
                    close={settingsClose}
                    updateUserPreferenceSettings={(language) => {
                      handleLanguageSelect(language);
                    }}
                  />
                )}
              </Suspense>
              <NetworkStatus />
              <ToastContent />
              {/* AI Assistant Embedded Panel */}
              <AIAssistantEmbeddedPanel
                assistantUrl={`${envConfig.aiAssistantUrl}/chat?agentId=${envConfig.asAgentId}`}
                isOpen={aiVisible}
                onClose={aiClose}
              />
            </ErrorBoundary>
          </Content>
        </ToastProvider>
      </SubscriptionManager>
    </ErrorBoundary>
  );
};

const App = () => {
  useEffect(() => {
    // With this method we dynamically load the catalogs
    dynamicActivate(defaultLocale);
  }, []);
  return (
    <ErrorBoundary>
      <EnvProvider envConfig={envConfig}>
        <ApolloProvider client={client}>
          <I18nProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
              <AppUserProvider>
                <ConfigProvider>
                  <Router>
                    <ErrorBoundary>
                      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                        <Routes>
                          <Route path="*" element={<AuthenticatedApp />} />
                          <Route path="auth/*" element={<Auth />} />
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </Router>
                </ConfigProvider>
              </AppUserProvider>
            </ThemeProvider>
          </I18nProvider>
        </ApolloProvider>
      </EnvProvider>
    </ErrorBoundary>
  );
};

export default hot(App);
